import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel, FormGroup, Grid, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import BannerMessage from "../components/BannerMessage";
import { Monad } from "../monad/Monad";
import { useHttpClientContext } from "../contexts/HttpClientContext";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnpublishedOutlinedIcon from '@mui/icons-material/UnpublishedOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { useAuthContext } from "../contexts/AuthContext";
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';

import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import "./AdministerBannersPage.css";

import StarterKit from "@tiptap/starter-kit";
import {
    LinkBubbleMenu,
    LinkBubbleMenuHandler,
    MenuButtonBold,
    MenuButtonBulletedList,
    MenuButtonEditLink,
    MenuButtonItalic,
    MenuButtonOrderedList,
    MenuButtonTextColor,
    MenuButtonUnderline,
    MenuControlsContainer,
    MenuDivider,
    RichTextEditor,
    type RichTextEditorRef,
} from "mui-tiptap";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Color from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import { useTranslation } from "react-i18next";

export interface ApiAdministrationBannerMessage {
    id?: number | undefined;
    title: string;
    message: string;
    type: string;
    priority: number;
    published: boolean;
    created_at: number;
}

const defaultNewBanner: ApiAdministrationBannerMessage = {
    created_at: new Date().getTime(),
    title: "En titel - krävs",
    message: "<p>Ett meddelande, ändra eller ta bort</p>",
    priority: 0,
    published: false,
    type: "success"
};

type ConfirmBannerStateAction = "new" | "update" | "delete";
interface ConfirmBannerState {
    banner: ApiAdministrationBannerMessage;
    action: ConfirmBannerStateAction
}

const AdministerBannersPage = () => {
    const httpClientContext = useHttpClientContext();
    const authContext = useAuthContext();
    const { t } = useTranslation();
    const [banners, setBanners] = useState<Monad<ApiAdministrationBannerMessage[]>>(Monad.idle());
    const [confirmBanner, setConfirmBanner] = useState<ConfirmBannerState | null>(null)
    const [newBanner, setNewBanner] = useState<ApiAdministrationBannerMessage>(defaultNewBanner);
    const [refreshCounter, setRefreshCounter] = useState(0);

    const [displayCreateNewBanner, setDisplayCreateNewBanner] = useState(true);

    const [translatedBanners, setTranslatedBanners] = useState<Monad<{ en: ApiAdministrationBannerMessage[], de: ApiAdministrationBannerMessage[] }>>(Monad.idle());

    const loadBanners = async (forceLoading: boolean = true) => {
        setBanners(Monad.loading());
        setTranslatedBanners(Monad.loading());

        try {
            const svPromise = httpClientContext.authenticatedHttpClient.get<ApiAdministrationBannerMessage[]>("administration/banner-messages");
            const enPromise = httpClientContext.authenticatedHttpClient.get<ApiAdministrationBannerMessage[]>("banner-messages/all", { headers: { lang: 'en' } });
            const dePromise = httpClientContext.authenticatedHttpClient.get<ApiAdministrationBannerMessage[]>("banner-messages/all", { headers: { lang: 'de' } });

            const [svRes, enRes, deRes] = await Promise.all([svPromise, enPromise, dePromise]);

            const enData = Array.isArray(enRes.data) ? enRes.data : (enRes.data as any).data || [];
            const deData = Array.isArray(deRes.data) ? deRes.data : (deRes.data as any).data || [];

            setBanners(Monad.success(svRes.data));
            setTranslatedBanners(Monad.success({ en: enData, de: deData }));
        } catch (error) {
            console.error("Error loading banners", error);
            setBanners(Monad.error("Ett fel inträffade när banners hämtades."));
            setTranslatedBanners(Monad.error("Ett fel inträffade när översättningar hämtades."));
        }
    }

    const onBannerChanged = (updatedBanner: ApiAdministrationBannerMessage) => {
        const prev = banners.getOrElse([]);
        setBanners(Monad.success(prev.map(existing => existing.id === updatedBanner.id ? updatedBanner : existing)));
    }

    const onConfirmBannerDialog = async (confirmedBannerState: ConfirmBannerState): Promise<ApiAdministrationBannerMessage> => {
        const prev = banners.getOrElse([]);
        switch (confirmedBannerState.action) {
            case "new":
                console.log("confirmed new, send data");
                const createResponse = await httpClientContext.authenticatedHttpClient.post<ApiAdministrationBannerMessage>("administration/banner-messages", confirmedBannerState.banner);
                prev.push(createResponse.data);
                setBanners(Monad.success(prev));
                setDisplayCreateNewBanner(false);
                // Refresh translations
                loadBanners();
                break;
            case "update":
                console.log("confirmed update, send data");
                const updateResponse = await httpClientContext.authenticatedHttpClient.put<ApiAdministrationBannerMessage>(`administration/banner-messages/${confirmedBannerState.banner.id!}`, confirmedBannerState.banner);
                setBanners(Monad.success(prev.map(existing => existing.id === updateResponse.data.id ? updateResponse.data : existing)));
                return updateResponse.data;
            case "delete":
                console.log("confirmed delete, send data");
                await httpClientContext.authenticatedHttpClient.delete(`administration/banner-messages/${confirmedBannerState.banner.id!}`);
                setBanners(Monad.success(prev.filter(existing => existing.id !== confirmedBannerState.banner.id)));
                //TODO: right now just sending back the existing deleted banner
                return confirmedBannerState.banner;
        }

        return Promise.resolve(confirmedBannerState.banner);
    }

    useEffect(() => {

        //TODO: check why we need this one ...
        if (httpClientContext.authenticatedClientReady) {
            authContext.user.match({
                loggedIn(_) {
                    console.log("Administer banners page, user logged in");
                    loadBanners();
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [httpClientContext.authenticatedClientReady]);

    return banners.match({
        loading: () =>
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="80vh"
            >
                <CircularProgress size="6rem" />
                <Typography variant="h6" style={{ marginTop: '16px' }}>
                    Loading banners ...
                </Typography>
            </Box>,
        error: (error) => <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="80vh"
        >
            <>
                <Typography variant="h5" gutterBottom>
                    Could not fetch banners
                </Typography>
                <Grid container spacing={2}>
                    <Grid>
                        <Button variant='contained' onClick={() => window.location.reload()}>{t("admin_banners.try_again")}</Button>
                    </Grid>
                </Grid>
            </>
        </Box>,
        success: (data) => (<>
            <Box m={2}>
                <Box p={2}>
                    <Typography variant="h4">{t("admin_banners.title")}</Typography>
                </Box>

                <Accordion>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={"panel-content-new-banner"}
                        id={"panel-header-new-banner"}
                    >
                        <Grid spacing={2} container alignItems="center" sx={{ width: "100%" }}>
                            <Grid size={{ xs: 2, sm: 1 }}>
                                <Typography variant="h6">
                                    <AddCircleOutlineOutlinedIcon />
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 10, sm: 11 }}>
                                <Typography variant="h6">
                                    {t("admin_banners.create_new")}
                                </Typography>
                            </Grid>
                        </Grid>
                    </AccordionSummary>
                    <AccordionDetails>
                        {displayCreateNewBanner &&
                            <>
                                <BannerMessage
                                    bannerMessage={newBanner}
                                />
                                <BannerForm
                                    onBannerChanged={(updated) => setNewBanner(updated)}
                                    onBannerSaving={(updated) => setConfirmBanner({
                                        action: "new",
                                        banner: updated
                                    })}
                                />
                            </>
                        }
                        {!displayCreateNewBanner &&
                            <Button variant="contained" onClick={() => {
                                setDisplayCreateNewBanner(true);
                                setNewBanner(defaultNewBanner);
                            }}>{t("admin_banners.create_new")}</Button>
                        }
                    </AccordionDetails>
                </Accordion>
                <Box p={2}>
                    <Typography variant="h5">{t("admin_banners.existing_banners")}</Typography>
                </Box>
                {data.sort((a, b) => b.created_at - a.created_at).map(banner =>
                    <Accordion key={`banner-accordion-${banner.id}`}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={"panel-content" + banner.id}
                            id={"panel-header-" + banner.id}
                        >
                            <Grid spacing={2} container alignItems="center" sx={{ width: "100%" }}>
                                <Grid size={{ xs: 2, sm: 1 }}>
                                    <Typography variant="h6">
                                        {banner.published
                                            ? <CheckCircleOutlinedIcon color="success" />
                                            : <UnpublishedOutlinedIcon color="error" />}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 10, sm: 11 }}>
                                    <Typography variant="h6">
                                        {banner.title}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </AccordionSummary>
                        <AccordionDetails>

                            <BannerMessage bannerMessage={{
                                id: banner.id,
                                title: banner.title,
                                message: banner.message,
                                type: banner.type,
                                priority: banner.priority
                            }} />

                            <BannerForm
                                existingBanner={banner}
                                onBannerChanged={onBannerChanged}
                                onBannerSaving={(updated) => setConfirmBanner({
                                    action: "update",
                                    banner: updated
                                })}
                                onBannerDeleting={(bannerToDelete) => setConfirmBanner({
                                    action: "delete",
                                    banner: bannerToDelete
                                })}
                            />
                            <TranslatedBanners 
                                bannerId={banner.id} 
                                translatedBanners={translatedBanners} 
                            />

                        </AccordionDetails>
                    </Accordion>
                )}
            </Box>
            <ConfirmSaveChangedDialog
                confirmBanner={confirmBanner}
                onClose={() => setConfirmBanner(null)}
                onConfirm={(confirmedBanner) => onConfirmBannerDialog(confirmedBanner)}
            />
        </>
        )
    })
}

const BannerForm = ({
    existingBanner,
    onBannerChanged,
    onBannerSaving,
    onBannerDeleting: onDeleteBanner,
}: {
    existingBanner?: ApiAdministrationBannerMessage
    onBannerChanged: (updatedBanner: ApiAdministrationBannerMessage) => void,
    onBannerSaving: (updatedBanner: ApiAdministrationBannerMessage) => void,
    onBannerDeleting?: (bannerToDelete: ApiAdministrationBannerMessage) => void
}) => {

    const { t } = useTranslation();
    const [bannerState, setBanner] = useState<ApiAdministrationBannerMessage>(defaultNewBanner);
    const bannerRef = useRef<ApiAdministrationBannerMessage>(defaultNewBanner);
    const rteRef = useRef<RichTextEditorRef>(null);
    const [bannerStateReady, setBannerStateReady] = useState(false);

    useEffect(() => {
        if (existingBanner) {
            setBanner(existingBanner);
            bannerRef.current = existingBanner;
            console.log("EDIT: existing banner", bannerRef.current);
        } else {
            console.log("NEW: creating new banner", bannerRef.current);
        }

        setBannerStateReady(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onTitleChanged = (title: string) => {
        bannerRef.current = { ...bannerRef.current, title: title };
        setBanner(bannerRef.current);
        onBannerChanged(bannerRef.current);
    }

    const onMessageChanged = (message: string) => {
        bannerRef.current = { ...bannerRef.current, message: message };
        setBanner(bannerRef.current);
        onBannerChanged(bannerRef.current);
    }
    const onPublishedChanged = (published: boolean) => {
        setBanner({ ...bannerState, published: published });
        bannerRef.current = { ...bannerRef.current, published: published };
        onBannerChanged(bannerRef.current);
    }
    const onTypeChanged = (type: string) => {
        bannerRef.current = { ...bannerRef.current, type: type };
        setBanner(bannerRef.current);
        onBannerChanged(bannerRef.current);
    }

    const onPriorityChanged = (priority: string) => {
        bannerRef.current = { ...bannerRef.current, priority: parseInt(priority) };
        setBanner(bannerRef.current);
        onBannerChanged(bannerRef.current);
    }

    const onSaveBanner = async () => {
        onBannerSaving(bannerState);
    }

    return <Stack mt={2}>
        <Grid container spacing={2} mb={2}>
            <Grid>
                <FormGroup>
                    <FormControlLabel control={<Checkbox onChange={evt => onPublishedChanged(evt.target.checked)} checked={bannerState.published} />} label={t("admin_banners.is_active")} />
                </FormGroup>
            </Grid>
            <Grid>
                <Box sx={{ width: "100%" }}>
                    <FormControl fullWidth>
                        <InputLabel id="demo-simple-select-label">{t("admin_banners.type_label")}</InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={bannerState.type}
                            label={t("admin_banners.type_label")}
                            onChange={evt => onTypeChanged(evt.target.value)}
                        >
                            <MenuItem value={"success"}>{t("admin_banners.type_success")}</MenuItem>
                            <MenuItem value={"info"}>{t("admin_banners.type_info")}</MenuItem>
                            <MenuItem value={"warning"}>{t("admin_banners.type_warning")}</MenuItem>
                            <MenuItem value={"error"}>{t("admin_banners.type_error")}</MenuItem>
                            <MenuItem value={"light"}>{t("admin_banners.type_light")}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Grid>
            <Grid size={"grow"}>
                <TextField
                    value={bannerState.priority}
                    type="number"
                    label="Prioritet"
                    onChange={evt => onPriorityChanged(evt.target.value)}
                />
            </Grid>
        </Grid>
        <Box mb={2}>
            <TextField
                sx={{ width: "100%" }}
                value={bannerState.title}
                label="Titel"
                onChange={evt => onTitleChanged(evt.target.value)}
            />
        </Box>

        {/*

            <Box mb={1}>
                <TextareaAutosize
                    style={{ width: "100%" }}
                    value={bannerState.message}
                    minRows={4}
                    onChange={evt => onMessageChanged(evt.target.value)}
                />
            </Box>
        */}
        {bannerStateReady &&
            <Box mb={2}>
                <RichTextEditor
                    onUpdate={evt => onMessageChanged(evt.editor?.getHTML())}
                    ref={rteRef}
                    extensions={[
                        StarterKit.configure({
                            heading: false, // Disable default heading
                        }),
                        LinkBubbleMenuHandler,
                        Link,
                        Underline,
                        Color,
                        TextStyle
                        /*Heading.configure({
                            levels: [3, 4]
                        })*/
                    ]} // Or any Tiptap extensions you wish!
                    content={bannerState.message} // Initial content for the editor
                    // Optionally include `renderControls` for a menu-bar atop the editor:
                    renderControls={() => (
                        <MenuControlsContainer>
                            {/*

                            <MenuSelectHeading labels={{
                                paragraph: "Paragraf",
                                heading1: "Rubrik 1",
                                heading2: "Rubrik 2",
                                heading3: "Rubrik 3",
                                heading4: "Rubrik 4",
                                heading5: "Rubrik 5",
                                heading6: "Rubrik 6"
                            }}
                                tooltipTitle="Text"
                            />
                            <MenuDivider />
                                */}
                            <MenuButtonBold tooltipLabel="Fetstil" />
                            <MenuButtonItalic tooltipLabel="Kursiv" />
                            <MenuButtonUnderline />
                            <MenuDivider />
                            <MenuButtonBulletedList tooltipLabel="Lista" />
                            <MenuButtonOrderedList tooltipLabel="Numrerad lista" />
                            <MenuDivider />
                            <MenuButtonEditLink tooltipLabel="Infoga länk" />
                            <LinkBubbleMenu
                                PopperProps={{ disablePortal: true }}
                                labels={{
                                editLinkAddTitle: "Infoga länk",
                                editLinkCancelButtonLabel: "Avbryt",
                                editLinkHrefInputLabel: "Länk",
                                editLinkSaveButtonLabel: "Infoga",
                                viewLinkEditButtonLabel: "Redigera",
                                viewLinkRemoveButtonLabel: "Ta bort",
                                editLinkEditTitle: "Redigera länk",
                                editLinkTextInputLabel: "Text"
                            }} />
                            <MenuDivider />
                            <MenuButtonTextColor
                                tooltipLabel="Textfärg"
                                labels={{
                                    cancelButton: "Avbryt",
                                    saveButton: "Ok",
                                    removeColorButton: "Återställ",
                                    removeColorButtonTooltipTitle: "Återställ till originalfärg",
                                    textFieldPlaceholder: "Välj färg (HEX)"
                                }}
                            />
                            {/* Add more controls of your choosing here */}
                        </MenuControlsContainer>
                    )}
                />
            </Box>
        }

        <Grid container spacing={1}>
            <Grid>
                <Box mb={2}>
                    <Button
                        onClick={() => onSaveBanner()}
                        style={{ width: "100%" }}
                        variant="contained">{t("admin_banners.save")}</Button>
                </Box>
            </Grid>
            <Grid>
                <Box mb={2}>
                    {existingBanner && onDeleteBanner &&
                        <Button
                            onClick={() => onDeleteBanner(existingBanner)}
                            style={{ width: "100%" }}
                            color="error"
                            variant="outlined">{t("admin_banners.delete")}</Button>
                    }
                </Box>
            </Grid>

        </Grid>

    </Stack>
}

const ConfirmSaveChangedDialog = ({
    confirmBanner,
    onClose,
    onConfirm,
}: {
    confirmBanner: ConfirmBannerState | null,
    onClose: () => void,
    onConfirm: (confirmedBannerState: ConfirmBannerState) => Promise<ApiAdministrationBannerMessage>
}) => {

    const { t } = useTranslation();
    const [confirmActionState, setConfirmActionState] = useState<Monad<ApiAdministrationBannerMessage>>(Monad.idle());

    useEffect(() => {
        setConfirmActionState(Monad.idle());
    }, [confirmBanner]);

    return (
        <React.Fragment>
            <Dialog
                onClose={() => {
                    onClose();
                    setConfirmActionState(Monad.idle());
                }}
                aria-labelledby="customized-dialog-title"
                open={confirmBanner !== null}
            >
                <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                    {confirmBanner && confirmBanner.action === "new" &&
                        <>{t("admin_banners.confirm_new")}</>
                    }
                    {confirmBanner && confirmBanner.action === "update" &&
                        <>{t("admin_banners.confirm_save")}</>
                    }
                    {confirmBanner && confirmBanner.action === "delete" &&
                        <>{t("admin_banners.confirm_delete")}</>
                    }
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={() => {
                        onClose();
                        setConfirmActionState(Monad.idle());
                    }}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent dividers>

                    {confirmBanner && confirmActionState.match({
                        idle() {
                            switch (confirmBanner.action) {
                                case "new":
                                    return (<>
                                        <Typography gutterBottom>{t("admin_banners.ask_new")}</Typography>
                                    </>);
                                case "update":
                                    return (<>
                                        <Typography gutterBottom>{t("admin_banners.ask_save")}</Typography>
                                    </>);
                                case "delete":
                                    return (<Typography gutterBottom>{t("admin_banners.ask_delete")}</Typography>);
                            }
                        },
                        loading: () =>
                            <Typography gutterBottom>
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <CircularProgress size="4rem" />
                                </Box>
                            </Typography>,
                        success(data) {
                            switch (confirmBanner.action) {
                                case "new":
                                    return (<>{t("admin_banners.success_new")}</>);
                                case "update":
                                    return (<>{t("admin_banners.success_save")}</>);
                                case "delete":
                                    return (<>{t("admin_banners.success_delete")}</>);
                            }
                        },
                        error: (error) => (<>{error}</>)
                    })}
                </DialogContent>
                <DialogActions>
                    {confirmBanner && confirmActionState.match({
                        idle: () => <>
                            <Button autoFocus onClick={() => {
                                onClose();
                                setConfirmActionState(Monad.idle());
                            }} variant="outlined" color="primary">
                                {t("admin_banners.no")}
                            </Button>
                            <Button autoFocus onClick={() => {
                                setConfirmActionState(Monad.loading());
                                onConfirm(confirmBanner)
                                    .then((data) => setConfirmActionState(Monad.success(data)))
                                    .catch((error) => {
                                        console.log("Error confirming banner", error);
                                        setConfirmActionState(Monad.error("Ett oväntat fel har inträffat."));
                                    });
                            }} variant="contained" color="primary">
                                {t("admin_banners.yes")}
                            </Button>
                        </>,
                        success: (data) => (<Button autoFocus onClick={() => {
                            onClose();
                            setConfirmActionState(Monad.idle());
                        }} variant="contained" color="primary">
                            Stäng
                        </Button>),
                        error: (error) => (<Button autoFocus onClick={() => {
                            onClose();
                            setConfirmActionState(Monad.idle());
                        }} variant="contained" color="primary">
                            Stäng
                        </Button>)
                    })}

                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

export default AdministerBannersPage;

const TranslatedBanners = ({ bannerId, translatedBanners }: { bannerId: number | undefined, translatedBanners: Monad<{ en: ApiAdministrationBannerMessage[], de: ApiAdministrationBannerMessage[] }> }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    const handleExpand = (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded);
    };

    if (!bannerId) return null;

    return (
        <Accordion expanded={expanded} onChange={handleExpand} sx={{ mt: 2, mb: 2, bgcolor: 'background.default' }} variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">{t("admin_banners.translations", "Översättningar (en, de)")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                {translatedBanners.match({
                    idle: () => <Typography color="text.secondary">Laddar...</Typography>,
                    loading: () => <Box display="flex" justifyContent="center" m={2}><CircularProgress size="2rem" /></Box>,
                    error: (err) => <Typography color="error">{err}</Typography>,
                    success: (data) => {
                        const enBanner = data.en.find((b: any) => b.festival_banner_messages_id == bannerId);
                        const deBanner = data.de.find((b: any) => b.festival_banner_messages_id == bannerId);

                        return (
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="subtitle2" color="text.secondary">English (en)</Typography>
                                    {enBanner && enBanner.message ? (
                                        <Box sx={{ mt: 1, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                            <Typography variant="h6">{enBanner.title}</Typography>
                                            <div dangerouslySetInnerHTML={{ __html: enBanner.message }} />
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">Ingen översättning hittades ännu.</Typography>
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="subtitle2" color="text.secondary">Deutsch (de)</Typography>
                                    {deBanner && deBanner.message ? (
                                        <Box sx={{ mt: 1, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                            <Typography variant="h6">{deBanner.title}</Typography>
                                            <div dangerouslySetInnerHTML={{ __html: deBanner.message }} />
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">Ingen översättning hittades ännu.</Typography>
                                    )}
                                </Grid>
                            </Grid>
                        )
                    }
                })}
            </AccordionDetails>
        </Accordion>
    );
}